(function cartRewardsModule(global, factory) {
  const api = factory(global);
  if (typeof module !== 'undefined' && module.exports) module.exports = api;
  if (global) global.CartRewards = api;
}(typeof window !== 'undefined' ? window : globalThis, (global) => {
  const initializedRoots = new WeakSet();
  const runtime = {
    roots: new Set(),
    listenersBound: false,
    inFlight: null,
    mutationPending: false,
    reconciliation: null,
    reconciling: false,
    lastWorkerSignature: null,
    workerChangedAt: 0,
    reconcileAgain: false,
    cartHasGifts: false,
  };
  // Codes issued by the free-gift Worker: FREEGIFT-… (older ones: GIFT-…) and codes standing in
  // for a BXGY code it took off the cart, named after it: TRY4FOR2-GIFTXXXXXXXX.
  const GIFT_CODE_PATTERN = /^(FREE)?GIFT-|-GIFT[A-Z0-9]{8}$/i;
  // Visible line property so checkout, emails and the order page show the line as a free gift.
  const GIFT_LABEL_PROPERTY = 'Gift';
  const GIFT_LABEL_VALUE = 'Free gift';
  // After the Worker changed the cart, the next reconciliation is its own refresh: no need to send
  // that cart back to the Worker (one request per cart change, no refresh loops).
  const WORKER_ECHO_WINDOW_MS = 10000;

  const normalizeId = (id) => String(id);

  const setGiftCardLoading = (card, loading) => {
    if (!card) return;
    const isLoading = Boolean(loading);
    card.dataset.loading = String(isLoading);
    card.classList?.toggle('is-loading', isLoading);
    card.setAttribute?.('aria-busy', String(isLoading));
    card.disabled = isLoading || card.dataset.available !== 'true';

    const action = card.querySelector?.('[data-gift-action]');
    if (action && !isLoading) {
      action.textContent = card.dataset.selected === 'true' ? 'ADDED ✓' : 'ADD';
    }
    if (action && isLoading) action.textContent = 'ADDING…';
  };

  const isGiftLine = (item, giftCampaignKey) => item?.properties?._gift_campaign === giftCampaignKey;

  const getGiftSelectionState = ({ cart, giftCampaignKey, giftQuantity }) => {
    const giftLines = (cart?.items || []).filter((item) => isGiftLine(item, giftCampaignKey));
    const selectedVariantIds = [];

    giftLines.forEach((line) => {
      if (line.variant_id == null) return;
      const variantId = normalizeId(line.variant_id);
      if (!selectedVariantIds.includes(variantId)) selectedVariantIds.push(variantId);
    });

    const quantity = Number(giftQuantity);
    const selectedCount = giftLines.reduce((sum, line) => sum + Number(line.quantity || 1), 0);

    return {
      selectedVariantIds,
      giftLines,
      selectedCount,
      canSelect: (quantity === 1 || quantity === 2)
        && selectedCount < quantity
        && selectedVariantIds.length === selectedCount,
    };
  };

  const calculateRewardState = ({ cart, freeShippingThreshold, giftThreshold, giftCampaignKey }) => {
    const giftLines = (cart.items || []).filter((item) => isGiftLine(item, giftCampaignKey));
    // After 4For2 and the free gift, before order codes (loyalty, DiscountYard), like the snippet.
    // The Worker's code is a product discount spread over the gift and the items it covers, so
    // with it applied the items subtotal is already what the non-gift items cost on their own.
    const linesTotal = (cart.items || []).reduce((sum, item) => sum + Number(item.final_line_price || 0), 0);
    const itemsSubtotal = Number(cart.items_subtotal_price ?? linesTotal);
    const giftCodeApplied = (cart.discount_codes || [])
      .some((discount) => discount.applicable !== false && GIFT_CODE_PATTERN.test(String(discount.code || '')));
    const rewardTotal = itemsSubtotal - (giftCodeApplied ? 0 : giftLines
      .reduce((sum, item) => sum + Number(item.final_line_price || 0), 0));
    const maximumThreshold = Math.max(Number(freeShippingThreshold || 0), Number(giftThreshold || 0));
    const progress = (threshold) => threshold > 0 ? Math.min(rewardTotal / threshold, 1) : 0;
    return {
      rewardTotal,
      maximumThreshold,
      freeShippingProgress: progress(freeShippingThreshold),
      giftProgress: progress(giftThreshold),
      freeShippingRemaining: Math.max(Number(freeShippingThreshold || 0) - rewardTotal, 0),
      giftRemaining: Math.max(Number(giftThreshold || 0) - rewardTotal, 0),
      freeShippingUnlocked: rewardTotal >= Number(freeShippingThreshold || 0),
      giftUnlocked: rewardTotal >= Number(giftThreshold || 0),
      giftLines,
    };
  };
  const selectGift = ({ selectedVariantIds, selectedGiftUnits, variantId, giftQuantity }) => {
    const selected = (selectedVariantIds || []).map((id) => normalizeId(id));
    const quantity = Number(giftQuantity);
    const selectedUnits = selectedGiftUnits == null
      ? selected.length
      : Number(selectedGiftUnits);
    const selectedUnitCount = Number.isFinite(selectedUnits) ? selectedUnits : selected.length;
    if (quantity !== 1 && quantity !== 2) return { accepted: false, selectedVariantIds: selected, reason: 'quantity-limit' };
    if (selected.includes(normalizeId(variantId))) return { accepted: false, selectedVariantIds: selected, reason: 'duplicate' };
    if (selectedUnitCount >= quantity) return { accepted: false, selectedVariantIds: selected, reason: 'quantity-limit' };
    selected.push(normalizeId(variantId));
    return { accepted: true, selectedVariantIds: selected, reason: null };
  };

  const getRootUrl = () => {
    const root = global?.Shopify?.routes?.root;
    if (!root) throw new Error('Shopify routes root is unavailable');
    return root.endsWith('/') ? root : `${root}/`;
  };

  const request = async (path, options = {}) => {
    const fetcher = global?.fetch;
    if (typeof fetcher !== 'function') throw new Error('Fetch is unavailable');

    const response = await fetcher.call(global, `${getRootUrl()}${path}`, options);
    if (!response.ok) throw new Error(`Cart request failed: ${response.status}`);
    return typeof response.json === 'function' ? response.json() : null;
  };

  const fetchCart = () => request('cart.js');

  const postCart = (path, payload) => request(path, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });

  const readConfig = (root) => {
    const dataset = root.dataset || {};
    const giftThreshold = Number(dataset.giftThreshold || 0);
    const giftQuantity = Number(dataset.giftQuantity || 0);
    const campaignKey = dataset.giftCampaignKey || '';
    const hasGiftConfiguration = Boolean(campaignKey)
      && giftThreshold > 0
      && (giftQuantity === 1 || giftQuantity === 2);
    const enabledAttribute = dataset.giftEnabled;

    return {
      autoDiscountCode: String(dataset.autoDiscountCode || '').trim(),
      autoDiscountProducts: String(dataset.autoDiscountProducts || '').split(',').filter(Boolean),
      autoDiscountMin: Number(dataset.autoDiscountMin || 4),
      freeShippingThreshold: Number(dataset.freeShippingThreshold || 0),
      giftThreshold,
      giftQuantity,
      giftCampaignKey: campaignKey,
      giftEnabled: hasGiftConfiguration && (enabledAttribute === undefined || enabledAttribute === 'true'),
    };
  };

  const taggedGiftLines = (cart, campaignKey) => (cart?.items || []).filter((item) => (
    campaignKey
      ? isGiftLine(item, campaignKey)
      : Boolean(item?.properties?._gift_campaign)
  ));

  const getGiftLineChanges = ({ giftLines = [], giftQuantity, removeAll = false }) => {
    let remainingUnits = removeAll ? 0 : Number(giftQuantity);
    if (!Number.isFinite(remainingUnits) || remainingUnits < 0) remainingUnits = 0;
    const retainedVariants = new Set();

    return giftLines.reduce((changes, line) => {
      const currentQuantity = Math.max(Number(line.quantity || 1), 0);
      const variantKey = line.variant_id == null
        ? `line:${line.key || ''}`
        : normalizeId(line.variant_id);
      const canRetain = !removeAll
        && currentQuantity > 0
        && remainingUnits > 0
        && !retainedVariants.has(variantKey);
      const retainedQuantity = canRetain ? 1 : 0;
      if (canRetain) retainedVariants.add(variantKey);
      remainingUnits = Math.max(remainingUnits - retainedQuantity, 0);

      if (line.key && retainedQuantity !== currentQuantity) {
        changes.push({ id: line.key, quantity: retainedQuantity });
      }
      return changes;
    }, []);
  };

  const calculateForConfig = (cart, config) => calculateRewardState({
    cart,
    freeShippingThreshold: config.freeShippingThreshold,
    giftThreshold: config.giftThreshold,
    giftCampaignKey: config.giftCampaignKey,
  });

  // Free-gift Worker (theme setting "Free gift Worker URL"): validates gifts against the subtotal
  // after 4For2 without the gifts and keeps a "GIFT-..." order discount on the cart.
  const getWorkerUrl = () => String(global?.theme?.settings?.freeGiftWorkerUrl || '').replace(/\/+$/, '');

  const hasGiftCode = (cart) => (cart?.discount_codes || [])
    .some((discount) => GIFT_CODE_PATTERN.test(String(discount.code || '')));

  // Dim the cart totals while the Worker re-prices the cart.
  const setSyncing = (syncing) => {
    if (!global.document) return;
    global.document.querySelectorAll('cart-form [data-merge="footer"], cart-form [data-merge="total"]')
      .forEach((el) => el.classList?.toggle('cart-rewards--syncing', syncing));
  };

  const workerSignature = (cart) => [
    (cart?.items || []).map((item) => `${item.key}:${item.quantity}:${item.final_line_price}`).join(','),
    (cart?.discount_codes || []).map((discount) => `${discount.code}:${discount.applicable}`).join(','),
  ].join('|');

  const syncWithWorker = async (cart) => {
    const workerUrl = getWorkerUrl();
    if (!workerUrl) return false;

    const signature = workerSignature(cart);
    const hasGifts = taggedGiftLines(cart, '').length > 0;
    const isWorkerEcho = runtime.workerChangedAt && Date.now() - runtime.workerChangedAt < WORKER_ECHO_WINDOW_MS;
    runtime.workerChangedAt = 0;
    if ((!hasGifts && !hasGiftCode(cart)) || signature === runtime.lastWorkerSignature || isWorkerEcho) {
      runtime.lastWorkerSignature = signature;
      return false;
    }

    setSyncing(true);
    try {
      const response = await global.fetch(`${workerUrl}/sync`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        // The lines/codes let the Worker start pricing before it has read the cart itself.
        body: JSON.stringify({
          cartToken: cart.token,
          lines: (cart.items || []).map((item) => ({
            variantId: `gid://shopify/ProductVariant/${item.variant_id}`,
            quantity: item.quantity,
            gift: Boolean(item.properties && item.properties._gift_campaign),
          })),
          codes: (cart.discount_codes || []).filter((discount) => discount.applicable).map((discount) => discount.code),
          country: global?.Shopify?.country,
        }),
      });
      if (!response.ok) throw new Error(`Free gift sync failed: ${response.status}`);
      const result = await response.json();

      runtime.lastWorkerSignature = result.changed ? null : signature;
      if (result.changed) runtime.workerChangedAt = Date.now();
      // Codes the Worker took off because Shopify would not apply the gift code with them (e.g. a
      // code not combining with product discounts): discountyard-bridge.js tells the customer.
      if (result.blockedCodes?.length && global.document && typeof global.CustomEvent === 'function') {
        global.document.dispatchEvent(new global.CustomEvent('free-gift:codes-blocked', {
          detail: { codes: result.blockedCodes },
        }));
      }
      return Boolean(result.changed);
    } finally {
      setSyncing(false);
    }
  };

  const removeCampaignGiftLines = async (cart, config) => {
    const giftLines = taggedGiftLines(cart, config.giftCampaignKey);
    // With the Worker the threshold is checked server side (the cart total here already includes
    // the effect of the gifts on 4For2), so only the quantity/duplicate rules are applied here.
    const thresholdMet = getWorkerUrl() ? true : calculateForConfig(cart, config).giftUnlocked;
    const removeAll = !(config.giftEnabled && thresholdMet);
    const changes = getGiftLineChanges({
      giftLines,
      giftQuantity: config.giftQuantity,
      removeAll,
    });

    for (const change of changes) {
      await postCart('cart/change.js', change);
    }
    return changes.length > 0;
  };

  const dispatchCartChange = () => {
    if (!global.document || typeof global.CustomEvent !== 'function') return;
    global.document.dispatchEvent(new global.CustomEvent('on:cart:change', {
      bubbles: true,
      cancelable: false,
    }));
  };

  // Auto-apply discount code (theme settings): add e.g. TRY4FOR2 once the cart holds enough items
  // from the eligible collection, unless the customer removed it from this cart.
  const AUTO_CODE_STORAGE_KEY = 'cart-rewards:auto-discount';
  const MAX_DISCOUNT_CODES = 5;

  const readAutoState = () => {
    try {
      return JSON.parse(global.sessionStorage?.getItem(AUTO_CODE_STORAGE_KEY) || 'null');
    } catch (error) {
      return null;
    }
  };

  const writeAutoState = (state) => {
    try {
      global.sessionStorage?.setItem(AUTO_CODE_STORAGE_KEY, JSON.stringify(state));
    } catch (error) {
      // Storage unavailable: the code may be re-added after a manual removal.
    }
  };

  const applyAutoDiscount = async (cart, config) => {
    const code = config.autoDiscountCode;
    if (!code) return false;

    const upper = code.toUpperCase();
    const codes = (cart?.discount_codes || []).map((discount) => String(discount.code || ''));
    // The code itself, or the Worker's stand-in for it (TRY4FOR2-GIFT…), is already on the cart.
    const present = codes.some((c) => c.toUpperCase() === upper || c.toUpperCase().startsWith(`${upper}-GIFT`));
    const eligible = new Set(config.autoDiscountProducts.map(String));
    const units = (cart?.items || [])
      .filter((item) => !item?.properties?._gift_campaign && eligible.has(String(item.product_id)))
      .reduce((sum, item) => sum + Number(item.quantity || 0), 0);

    const state = readAutoState();
    const sameCart = state && state.token === cart.token && state.code === upper;
    if (present) {
      if (!sameCart || !state.applied) writeAutoState({ token: cart.token, code: upper, applied: true, declined: false });
      return false;
    }
    if (sameCart && state.declined) return false;
    if (sameCart && state.applied) {
      // It was on this cart and is gone. With enough items the customer removed it: respect that.
      // Otherwise the cart was emptied/reduced and the code may be added again later.
      writeAutoState(units >= config.autoDiscountMin ? { ...state, declined: true } : { ...state, applied: false });
      return false;
    }
    if (units < config.autoDiscountMin || codes.length >= MAX_DISCOUNT_CODES) return false;
    // Two DiscountYard codes already fill the order's code limit: keep the customer's codes.
    if (global.DiscountYardBridge && !global.DiscountYardBridge.canAddCode(cart, code)) return false;

    await postCart('cart/update.js', { discount: [...codes, code].join(',') });
    writeAutoState({ token: cart.token, code: upper, applied: true, declined: false });
    return true;
  };

  const reconcileCart = async (cart) => {
    runtime.cartHasGifts = taggedGiftLines(cart, '').length > 0 || hasGiftCode(cart);
    let removed = false;
    const configs = new Map();
    const connectedRoots = [...runtime.roots].filter((root) => root.isConnected !== false);
    const roots = connectedRoots.length ? connectedRoots : [...runtime.roots];

    roots.forEach((root) => {
      const config = readConfig(root);
      const key = config.giftCampaignKey || '__all-tagged-gifts__';
      if (!configs.has(key)) configs.set(key, config);
    });

    for (const config of configs.values()) {
      if (await removeCampaignGiftLines(cart, config)) removed = true;
    }
    // After a change the change event reconciles the new cart, which syncs it then.
    if (removed) return true;
    const [firstConfig] = configs.values();
    if (firstConfig && await applyAutoDiscount(cart, firstConfig)) return true;
    return syncWithWorker(cart);
  };

  const scheduleReconciliation = () => {
    if (runtime.inFlight || runtime.reconciling) return runtime.reconciliation;
    if (runtime.reconciliation) {
      // The cart changed while a reconciliation is running: check the final cart afterwards too.
      runtime.reconcileAgain = true;
      return runtime.reconciliation;
    }

    runtime.reconciliation = (async () => {
      const cart = await fetchCart();
      const removed = await reconcileCart(cart);
      if (removed) {
        runtime.reconciling = true;
        try {
          dispatchCartChange();
        } finally {
          runtime.reconciling = false;
        }
      }
    })().finally(() => {
      runtime.reconciliation = null;
      if (runtime.reconcileAgain) {
        runtime.reconcileAgain = false;
        scheduleReconciliation()?.catch((error) => {
          if (global.console?.error) global.console.error(error);
        });
      }
    });

    return runtime.reconciliation;
  };

  const runGiftMutation = (mutation) => {
    if (runtime.inFlight) return runtime.inFlight;

    runtime.inFlight = (async () => {
      await mutation();
      const cart = await fetchCart();
      await reconcileCart(cart);
      runtime.reconciling = true;
      try {
        dispatchCartChange();
      } finally {
        runtime.reconciling = false;
      }
    })().finally(() => {
      runtime.inFlight = null;
    });

    return runtime.inFlight;
  };

  const findGiftLine = (cart, variantId, campaignKey) => taggedGiftLines(cart, campaignKey)
    .find((line) => normalizeId(line.variant_id) === normalizeId(variantId));

  const handleGiftClick = async (root, card) => {
    const config = readConfig(root);
    if (!config.giftEnabled || card.dataset.available !== 'true') return;

    const cart = await fetchCart();
    const state = calculateForConfig(cart, config);
    const selection = getGiftSelectionState({
      cart,
      giftCampaignKey: config.giftCampaignKey,
      giftQuantity: config.giftQuantity,
    });
    const variantId = card.dataset.variantId;
    const existingLine = findGiftLine(cart, variantId, config.giftCampaignKey);

    if (existingLine || card.dataset.selected === 'true') {
      const lineKey = card.dataset.lineKey || existingLine?.key;
      if (!lineKey) return;
      await runGiftMutation(() => postCart('cart/change.js', { id: lineKey, quantity: 0 }));
      return;
    }

    if (!state.giftUnlocked) return;
    if (!selection.canSelect) return;
    const selectionResult = selectGift({
      selectedVariantIds: selection.selectedVariantIds,
      selectedGiftUnits: selection.selectedCount,
      variantId,
      giftQuantity: config.giftQuantity,
    });
    if (!selectionResult.accepted) return;

    await runGiftMutation(() => postCart('cart/add.js', {
      items: [{
        id: variantId,
        quantity: 1,
        properties: { _gift_campaign: config.giftCampaignKey, [GIFT_LABEL_PROPERTY]: GIFT_LABEL_VALUE },
      }],
    }));
  };

  const bindRoot = (root) => {
    if (!root || initializedRoots.has(root)) return;
    initializedRoots.add(root);
    runtime.roots.add(root);

    root.addEventListener('click', (event) => {
      const card = event.target.closest?.('[data-gift-variant]');
      if (!card || !root.contains(card)) return;
      if (runtime.inFlight || runtime.mutationPending || card.dataset.loading === 'true') return;
      event.preventDefault();
      runtime.mutationPending = true;
      setGiftCardLoading(card, true);
      handleGiftClick(root, card).catch((error) => {
        if (global.console?.error) global.console.error(error);
      }).finally(() => {
        runtime.mutationPending = false;
        if (card.isConnected !== false) setGiftCardLoading(card, false);
      });
    });
  };

  const discoverRoots = () => {
    if (!global.document) return 0;
    const roots = global.document.querySelectorAll('[data-cart-rewards]');
    roots.forEach(bindRoot);
    return roots.length;
  };

  const bindDocumentListeners = () => {
    if (!global.document || runtime.listenersBound) return;
    runtime.listenersBound = true;
    const refresh = () => {
      const rootCount = discoverRoots();
      const reconciliation = rootCount > 0 ? scheduleReconciliation() : null;
      if (!reconciliation) return;
      reconciliation.catch((error) => {
        if (global.console?.error) global.console.error(error);
      });
    };
    global.document.addEventListener('on:cart:change', refresh);
    global.document.addEventListener('on:cart:after-merge', refresh);

    // Before leaving for checkout: wait for a pending gift update, and re-check carts with a gift
    // (another app, e.g. the DiscountYard code field, may have changed the codes without the theme
    // knowing), so the gift codes match the cart. DiscountYard renames the button it takes over.
    global.document.addEventListener('click', (event) => {
      const trigger = event.target.closest?.('a[href$="/checkout"], button[name="checkout"], [name="stack-discounts-redirect"]');
      if (!trigger || trigger.dataset.giftReady) return;
      const pending = runtime.inFlight || runtime.reconciliation
        || (getWorkerUrl() && runtime.cartHasGifts ? scheduleReconciliation() : null);
      if (!pending) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      trigger.classList.add('is-loading');
      pending.catch(() => {}).finally(() => {
        trigger.classList.remove('is-loading');
        trigger.dataset.giftReady = 'true';
        trigger.click();
        delete trigger.dataset.giftReady;
      });
    }, true);
  };

  const init = () => {
    const rootCount = discoverRoots();
    bindDocumentListeners();
    if (rootCount === 0) return;
    const reconciliation = scheduleReconciliation();
    if (reconciliation) {
      reconciliation.catch((error) => {
        if (global.console?.error) global.console.error(error);
      });
    }
  };

  const api = {
    calculateRewardState,
    getGiftLineChanges,
    getGiftSelectionState,
    isGiftLine,
    selectGift,
    setGiftCardLoading,
    init,
  };

  if (global.document) init();
  return api;
}));
